import {useEffect, useState, useRef} from 'react';
import MUIDataTable, { MUIDataTableProps, MUIDataTableColumn, MUIDataTableColumnOptions, MUIDataTableOptions, MUISortOptions } from 'mui-datatables';

import TableCell from '@mui/material/TableCell';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { Tooltip, TableSortLabel } from '@mui/material';

interface CustomMUIDataTableColumnOptions extends MUIDataTableColumnOptions {
    
}
interface CustomMUIDataTableColumn extends MUIDataTableColumn {
    options?: CustomMUIDataTableColumnOptions | undefined;
    isResize?:boolean;
    fixed?:boolean;
    minWidth?:number;
    width?:number;
    height?:number;
}

interface CustomMUIDataTableOptions extends MUIDataTableOptions {

}

export type CustomMUIDataTableColumnDef = CustomMUIDataTableColumn | string;

interface CustomMUIDataTableProps extends MUIDataTableProps {
    columns: CustomMUIDataTableColumn[]
}

interface ColumnAttrValMap {
  [key: number]: number;
}

export default function CustomMUIDataTable(prop:CustomMUIDataTableProps){
    const {columns, options, title, data, components, innerRef} = prop;
    const domId = `${Math.random().toString(36).slice(2, 10)}-customMUIDataTable`;
    const muiTableOuterRef = useRef<HTMLDivElement>(null);
    const isRowSelectable = prop?.options?.selectableRows !== 'none';
    const offset = isRowSelectable ? 1 : 0;
    const [customColumns, setCustomColumns] = useState<CustomMUIDataTableColumn[]>([]);
    const [customTableOptions, setCustomTableOptions] = useState<CustomMUIDataTableOptions>({});
    const [columnWidths, setColumnWidths] = useState<ColumnAttrValMap>({});
    const [sortOrder, setSortOrder] = useState<MUISortOptions>({
        name: "",
        direction: "asc",
    });
    
    const handleResize = (e:any, index: number, width: number) => {
        setColumnWidths(prev => ({
            ...prev,
            [(index).toString()]: width,
        }));
    };

    const onSort = (e:any, index:any) => {
        if (customColumns) {
            let col = customColumns[index];
            let order:any = {
                name: `${col.name}`,
                direction: sortOrder.direction === "asc"?"desc":"asc"
            };
            setSortOrder(order);

            if( options?.sort === true && col?.options?.sort !== false && options?.onColumnSortChange){
                options?.onColumnSortChange(order.name, order.direction);
            }

        }
    };

    const CustonMuiTableStyle = (prop:{customColumns:CustomMUIDataTableColumnDef[],domId:string, columnWidths:ColumnAttrValMap}) => {
        const [custonMuiTableStyle, setCustonMuiTableStyle] = useState<String>("");

        const resetStyle = () => {
            const headThs:NodeListOf<any>|undefined = muiTableOuterRef?.current?.querySelectorAll('.MuiTableHead-root th');
            let vc:any = [];
            let styleText = `
            .react-resizable-handle-e {
                right: -16px;
            }

            .MuiTableCell-head {

            }
            `;
            if( muiTableOuterRef?.current?.offsetWidth && muiTableOuterRef?.current?.offsetWidth >= 768 ) {
                customColumns.forEach((column, index) => {
                    if ( typeof column != 'string' && column?.options?.display !== 'excluded' && column?.options?.display !== 'false' && column?.options?.display !== false){
                        vc.push(column);
                        if (column?.fixed === true){
                            let offsetLeft = (vc.length-offset) === 0
                                ? headThs?.item(vc.length-offset)?.offsetWidth
                                : headThs?.item(vc.length)?.offsetLeft;
    
                            styleText += `
                                div[data-scope="${domId}"] .MuiTableHead-root .MuiTableCell-root:nth-child(${vc.length + offset}) {
                                        position: sticky !important;
                                        left: ${offsetLeft}px !important;
                                        z-index: ${customColumns.length-index} !important;
                                }
                                div[data-scope="${domId}"] .MuiTableBody-root .MuiTableCell-root:nth-child(${vc.length + offset}) {
                                        position: sticky !important;
                                        left: ${offsetLeft}px !important;
                                        z-index: ${customColumns.length-index-1} !important;
                                }
                            `;
                        }
                    }
                });
            }
            setCustonMuiTableStyle(styleText);
        };

        useEffect(()=>{
            resetStyle();
            window.addEventListener('resize', resetStyle);

            return () => {
                window.removeEventListener('resize', resetStyle);
            }
        }, []);

        useEffect(()=>{
            resetStyle();
        }, [prop.columnWidths]);

        return (
            <>
                <style>
                    {custonMuiTableStyle}
                </style>
            </>
        );
    };
    
    const isCustomMUIDataTableColumn = (col: any): col is CustomMUIDataTableColumn => {
        return typeof col === "object" && col !== null && "isResize" in col;
    };
    const customHeadRender = (columnMeta:any, handleSort:any) => {
        const index = columnMeta.index;
        let strLength = columnMeta.label.length - (columnMeta.label.match(/[\u0020-\u007E]/g) || []).length;

        if( !('width' in columnMeta) ){
            columnMeta.width = columnWidths[index+offset]??(strLength*12+20);
        }
        if( !('height' in columnMeta) ){
            columnMeta.height = 30;
        }
        if( !('minWidth' in columnMeta) ){
            columnMeta.minWidth = strLength*12+20;
        }

        return (
            <TableCell 
                key={index+offset}
                data-key={index+offset}
            >
                <ResizableBox
                    width={columnMeta.width}
                    height={24}
                    axis="x"
                    minConstraints={[columnMeta?.minWidth, 24]}
                    resizeHandles={['e']}
                    onResizeStop={(e, data) => handleResize(e, index+offset, data.size.width)}
                >
                    {
                        columnMeta?.sort !== false
                        ?
                            <Tooltip title={`Sort`}>
                                <TableSortLabel
                                    active={sortOrder.name === columnMeta.name}
                                    direction={sortOrder.direction}
                                    onClick={(e:any)=>{ onSort(e, index) }}
                                >
                                    <div 
                                        style={{ 
                                            width: "100%",
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            cursor: "pointer",
                                            fontWeight: 600,
                                            userSelect: "none",
                                        }}
                                    >
                                        {columnMeta.label}
                                    </div>
                                </TableSortLabel>
                            </Tooltip>
                        :
                            <div 
                                style={{ 
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: 600,
                                    userSelect: "none",
                                }}
                            >
                                {columnMeta.label}
                            </div>
                    }
                </ResizableBox>
            </TableCell>
        );
    };

    useEffect(()=>{
        const container:any = muiTableOuterRef?.current?.querySelector('[class*="MUIDataTable-responsiveBase"]');

        if (!container) {
            return;
        }

        const onScroll = (evt:Event) => {

        };

        container?.addEventListener('scroll', onScroll);

        return () => {
            container?.removeEventListener('scroll', onScroll);
        }
    }, []);

    useEffect(()=>{
        setCustomColumns(columns.map((col, index)=>{
            let newOptions = {
                ...col?.options,
                sort: col.options?.sort ?? customTableOptions?.sort??false,
                sortDescFirst: col.options?.sortDescFirst ?? false,
                filter: col.options?.filter ?? true,
            };
            if ( isCustomMUIDataTableColumn(col) ) {
                newOptions = {
                    ...newOptions,
                    customHeadRender: customHeadRender,
                    setCellProps: newOptions?.setCellProps ?? (() => ({
                        style: { maxWidth: "150px" }
                    }))
                }
            }

            return {
                ...col,
                options: {
                    ...newOptions
                }
            }; 
        }));

        setCustomTableOptions({
            ...options,
            sortOrder
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, options, sortOrder]);

    return (
        <>
            <div data-scope={domId} ref={muiTableOuterRef}>
                <CustonMuiTableStyle domId={domId} customColumns={customColumns} columnWidths={columnWidths} />
                <MUIDataTable
                    title={title}
                    data={data}
                    columns={customColumns}
                    options = {customTableOptions}
                    components={components}
                    innerRef={innerRef}
                />
            </div>
        </>
    );
}